import type { CustomerOrder } from '../../customerOrdersApi'
import type { OrderProgressStep } from './dashboardShared'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import { formatDateTime } from '../../helpers/formatters'
import { toOrderStatusBadgeClasses, toOrderStatusLabel } from '../../helpers/status'
import { toOrderStateDescription } from './dashboardShared'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type DashboardTrackingSelectedOrderCardProps = {
  selectedOrder: CustomerOrder
  selectedOrderNextStep: OrderProgressStep | null
  language: ShopLanguage
  locale: ShopLocale
  isItalian: boolean
  t: TranslateFn
}

export const DashboardTrackingSelectedOrderCard = ({
  selectedOrder,
  selectedOrderNextStep,
  language,
  locale,
  isItalian,
  t,
}: DashboardTrackingSelectedOrderCardProps) => (
  <div className="ca-surface-muted ca-theme-transition rounded-lg px-4 py-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-(--color-text-muted)">
          {isItalian ? 'Ordine selezionato' : 'Selected order'}
        </p>
        <p className="mt-1 text-base font-semibold text-(--color-text-primary)">{selectedOrder.orderNumber}</p>
      </div>
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${toOrderStatusBadgeClasses(
          selectedOrder.status,
        )}`}
      >
        {toOrderStatusLabel(selectedOrder.status, language)}
      </span>
    </div>
    <p className="mt-2.5 text-sm text-(--color-text-secondary)">{toOrderStateDescription(selectedOrder, language)}</p>

    <dl className="mt-6 grid gap-5 text-sm sm:grid-cols-2">
      <div>
        <dt className="text-xs uppercase tracking-wide text-(--color-text-muted)">{isItalian ? 'Data ordine' : 'Order date'}</dt>
        <dd className="mt-0.5 font-medium text-(--color-text-primary)">
          {formatDateTime(selectedOrder.createdAt, locale, t('common.never'))}
        </dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide text-(--color-text-muted)">
          {isItalian ? 'Ultimo aggiornamento' : 'Last update'}
        </dt>
        <dd className="mt-0.5 font-medium text-(--color-text-primary)">
          {formatDateTime(selectedOrder.updatedAt, locale, t('common.never'))}
        </dd>
      </div>
      {selectedOrder.status === 'shipped' ? (
        <div>
          <dt className="text-xs uppercase tracking-wide text-(--color-text-muted)">{isItalian ? 'Tracking' : 'Tracking'}</dt>
          <dd className="mt-0.5 font-medium text-(--color-text-primary)">
            {selectedOrder.trackingCode || (isItalian ? 'In aggiornamento' : 'Updating')}
          </dd>
        </div>
      ) : null}
      <div>
        <dt className="text-xs uppercase tracking-wide text-(--color-text-muted)">{isItalian ? 'Prossimo step' : 'Next step'}</dt>
        <dd className="mt-0.5 font-medium text-(--color-text-primary)">
          {selectedOrderNextStep
            ? selectedOrderNextStep.label
            : isItalian
              ? 'Nessuna azione richiesta'
              : 'No pending action'}
        </dd>
      </div>
    </dl>
  </div>
)
